/**
 * Fifty Rooms ready-made sets (sections/fr-bundle-set.liquid).
 * - gallery thumbs switch the main slide;
 * - ticking / unticking an item or changing its size recalculates the total with the bundle tiers
 *   (data-tier-2 / data-tier-3, same values as the automatic discounts) and rewrites the button and nudge lines;
 * - the button adds every ticked item in ONE POST /cart/add.js (properties._bundle = set title), then announces the
 *   cart change the way fr-pdp.js does (CartLinesUpdateEvent from @shopify/events opens the cart drawer).
 */
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** Like Liquid money_without_trailing_zeros, with the shop money format. @param {number} cents @param {string} format */
function money(cents, format) {
  const whole = Math.round(cents) % 100 === 0;
  const amount = (Math.round(cents) / 100).toFixed(whole ? 0 : 2);
  const [a, b] = amount.split('.');
  const grouped = a.replace(/\d(?=(\d{3})+(?!\d))/g, '$&,');
  const value = b ? `${grouped}.${b}` : grouped;
  return /{{\s*\w+\s*}}/.test(format || '') ? format.replace(/{{\s*\w+\s*}}/, value) : `$${value}`;
}

/** @param {string} tpl @param {Record<string, string | number>} map */
const fill = (tpl, map) => Object.entries(map).reduce((t, [k, v]) => t.split(`[${k}]`).join(String(v)), tpl || '');

class FrBundleSet extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', () => this.update());
    this.querySelector('[data-fr-bset-add]')?.addEventListener('click', () => this.add());
    const section = this.closest('.fr-bset');
    section?.querySelectorAll('[data-fr-bset-thumb]').forEach((t) =>
      t.addEventListener('click', () => {
        const n = /** @type {HTMLElement} */ (t).dataset.frBsetThumb;
        section.querySelectorAll('[data-fr-bset-slide]').forEach((s) => {
          /** @type {HTMLElement} */ (s).hidden = /** @type {HTMLElement} */ (s).dataset.frBsetSlide !== n;
        });
        section.querySelectorAll('[data-fr-bset-thumb]').forEach((x) => x.classList.toggle('is-on', x === t));
      })
    );
    this.update();
  }

  /** @returns {{id: number, price: number}[]} */
  selected() {
    return [...this.querySelectorAll('[data-fr-bset-item]')]
      .filter((row) => /** @type {HTMLInputElement} */ (row.querySelector('[data-fr-bset-on]')).checked)
      .map((row) => {
        const v = /** @type {HTMLInputElement | HTMLSelectElement} */ (row.querySelector('[data-fr-bset-variant]'));
        const price = v instanceof HTMLSelectElement ? v.selectedOptions[0].dataset.price : v.dataset.price;
        return { id: Number(v.value), price: Number(price) };
      });
  }

  update() {
    const d = this.dataset;
    const f = d.moneyFormat || '';
    this.querySelectorAll('[data-fr-bset-item]').forEach((row) => {
      const on = /** @type {HTMLInputElement} */ (row.querySelector('[data-fr-bset-on]')).checked;
      row.classList.toggle('is-off', !on);
      const v = row.querySelector('[data-fr-bset-variant]');
      const price = v instanceof HTMLSelectElement ? v.selectedOptions[0].dataset.price : /** @type {HTMLElement} */ (v).dataset.price;
      const el = row.querySelector('[data-fr-bset-iprice]');
      if (el) el.textContent = money(Number(price), f);
    });
    const items = this.selected();
    const n = items.length;
    const full = items.reduce((s, i) => s + i.price, 0);
    const pct = n >= 3 ? Number(d.tierThree) : n === 2 ? Number(d.tierTwo) : 0;
    const total = Math.round((full * (100 - pct)) / 100);
    const save = full - total;
    const set = (sel, text) => {
      const el = this.querySelector(sel);
      if (el) el.textContent = text;
    };
    set('[data-fr-bset-total]', money(total, f));
    set('[data-fr-bset-full]', save > 0 ? money(full, f) : '');
    const badge = /** @type {HTMLElement | null} */ (this.querySelector('[data-fr-bset-badge]'));
    if (badge) {
      badge.hidden = save <= 0;
      // rule of 100 (reports/Психология цен для Fifty Rooms.md): under $100 the % looks bigger, above it the dollars
      badge.textContent = total < 10000 ? `Save ${pct}%` : `Save ${money(save, f)}`;
    }
    const btn = /** @type {HTMLButtonElement} */ (this.querySelector('[data-fr-bset-add]'));
    btn.disabled = n === 0;
    const all = this.querySelectorAll('[data-fr-bset-item]').length;
    const tpl = n === all ? d.tButton : n === 1 ? d.tButtonOne : d.tButtonSome;
    btn.textContent = fill(tpl, { count: n, total: money(total, f) });
    // the whole set (even a 2-piece one) gets the «you save» line; «tick one more» only when a piece is unticked
    const nudge = (n >= 3 || (n === all && save > 0)) ? d.tSave3 : n === 2 ? d.tSave2 : n === 1 ? d.tSave1 : '';
    set('[data-fr-bset-nudge]', fill(nudge, { save: money(save, f), pct }));
    const left = Number(d.freeShip) - total;
    set('[data-fr-bset-ship]', n === 0 ? '' : left > 0 ? fill(d.tShipLeft, { left: money(left, f) }) : d.tShipOk || '');
  }

  async add() {
    const items = this.selected().map((i) => ({ id: i.id, quantity: 1, properties: { _bundle: this.dataset.bundle || '' } }));
    if (!items.length) return;
    const btn = /** @type {HTMLButtonElement} */ (this.querySelector('[data-fr-bset-add]'));
    const err = /** @type {HTMLElement} */ (this.querySelector('[data-fr-bset-error]'));
    err.hidden = true;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    /** @type {any} */
    let events = {};
    try {
      events = await import('@shopify/events');
    } catch {}
    const CartEvent = events.CartLinesUpdateEvent;
    const deferred = CartEvent?.createPromise ? CartEvent.createPromise() : null;
    if (CartEvent && deferred) {
      this.dispatchEvent(
        new CartEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })),
          promise: deferred.promise,
        })
      );
    }
    try {
      const body = { items, ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}) };
      const r = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok || json.status) throw new Error(json.description || json.message || `cart/add.js ${r.status}`);
      const cart = await fetch(`${ROOT}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((x) => x.json());
      deferred?.resolve({
        cart: CartEvent?.createCartFromAjaxResponse ? CartEvent.createCartFromAjaxResponse(cart) : cart,
        detail: { items: cart.items, source: 'fr-bundle-set', sourceId: this.id || 'fr-bundle-set', itemCount: items.length, sections: json.sections || undefined, didError: false },
      });
      if (!CartEvent) {
        document.dispatchEvent(new CustomEvent('fr:cart-updated', { detail: { cart } }));
        window.location.href = `${ROOT}cart`;
      }
    } catch (e) {
      deferred?.resolve({ cart: null, detail: { didError: true, source: 'fr-bundle-set', itemCount: 0 } });
      err.textContent = /** @type {Error} */ (e).message;
      err.hidden = false;
    } finally {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('fr-bundle-set')) customElements.define('fr-bundle-set', FrBundleSet);
